/**
 * La escala de antigüedad de la cobranza y los pagos: fuente única.
 *
 * ─── 🔴 Por qué existe ────────────────────────────────────────────────────────
 *
 * Hasta el 2026-09-30 la escala estaba escrita en CINCO pantallas (aging de
 * cobranza y de pagos, Centro de Reportes, informe de compras, pagos a
 * proveedores y cobranza) y no coincidían: 1-30 era amarillo en cuatro y ámbar
 * en una, y en dos el tramo **+90 salía en rosa claro, MÁS SUAVE que 61-90**.
 * La deuda más vieja era la que menos llamaba la atención. Los rótulos
 * tampoco: «Vigente» / «Al día», «90+» / «+90» / «>90».
 *
 * La escala tiene que subir de gravedad en cada peldaño: verde → amarillo →
 * naranja → rojo → rojo más fuerte. `agingBuckets.test.ts` lo fija, y
 * `agingCatalogoUnico.test.ts` falla si una pantalla vuelve a declarar la suya.
 *
 * Los colores de los dos peldaños intermedios salen de la familia `aging-*` de
 * `index.css`; los extremos reusan `success` y `danger`, que ya son eso.
 */

export type AgingBucketKey = 'current' | '1_30' | '31_60' | '61_90' | 'over_90'

export interface AgingBucket {
  key: AgingBucketKey
  /** Rótulo completo, para tarjetas y pastillas. */
  label: string
  /** Rótulo corto, para encabezados de columna. */
  short: string
  /** Pastilla de una celda: fondo marcado + texto. */
  chip: string
  /** Tarjeta de resumen: fondo suave + borde + texto. */
  card: string
  /** Solo el color del texto, para un monto dentro de una tabla. */
  text: string
  /** Relleno de una barra de proporción. */
  bar: string
}

export const AGING_BUCKETS: readonly AgingBucket[] = [
  {
    key: 'current', label: 'Al día', short: 'Al día',
    chip: 'bg-success-surface-strong text-success-content',
    card: 'bg-success-surface border-success-border text-success-content',
    text: 'text-success-content',
    bar: 'bg-success-solid',
  },
  {
    key: '1_30', label: '1-30 días', short: '1-30',
    chip: 'bg-aging-1-surface-strong text-aging-1-content',
    card: 'bg-aging-1-surface border-aging-1-border text-aging-1-content',
    text: 'text-aging-1-content',
    bar: 'bg-aging-1-solid',
  },
  {
    key: '31_60', label: '31-60 días', short: '31-60',
    chip: 'bg-aging-2-surface-strong text-aging-2-content',
    card: 'bg-aging-2-surface border-aging-2-border text-aging-2-content',
    text: 'text-aging-2-content',
    bar: 'bg-aging-2-solid',
  },
  {
    key: '61_90', label: '61-90 días', short: '61-90',
    chip: 'bg-danger-surface-strong text-danger-content',
    card: 'bg-danger-surface border-danger-border text-danger-content',
    text: 'text-danger-content',
    bar: 'bg-danger-solid',
  },
  {
    // Un peldaño más que 61-90 en las cuatro variantes: el fondo de la
    // pastilla es el `border` (red-200 en claro, red-800 en oscuro), que es
    // lo que la distingue de la anterior.
    key: 'over_90', label: '+90 días', short: '+90',
    chip: 'bg-danger-border text-danger-content-strong',
    card: 'bg-danger-surface-strong border-danger-border text-danger-content-strong',
    text: 'text-danger-content-strong',
    bar: 'bg-danger-content',
  },
] as const

const POR_CLAVE = Object.fromEntries(AGING_BUCKETS.map(b => [b.key, b])) as Record<AgingBucketKey, AgingBucket>

/**
 * El tramo por su clave del backend.
 *
 * ⚠️ Una clave desconocida NO cae a «Al día»: la pintaría de verde, que es
 * justo el color que dice «no hay problema». Sale en gris con la clave a la
 * vista, para que el hueco se note.
 */
export function agingBucket(key: string): AgingBucket {
  return POR_CLAVE[key as AgingBucketKey] ?? {
    key: key as AgingBucketKey, label: key, short: key,
    chip: 'bg-glass-3 text-content-muted',
    card: 'bg-glass-3 border-divider text-content-muted',
    text: 'text-content-muted',
    bar: 'bg-content-subtle',
  }
}
